import {
    NivalizInboundTransactionTimerActivityWeekdayHeatmapChart,
    NivalizOutboundTransactionTimerActivityWeekdayHeatmapChart,
    hasChartSeries
} from '@frontend/chart/components';
import { CommonMessage } from '@frontend/lang';
import { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';

import { useInsightHeatmapCellClick } from '../heatmap-cell-summary/use-insight-heatmap-cell-click';
import { InsightCardWidth } from '../insight-card-width';
import { InsightCard } from '../insight-card/insight-card.component';
import { useInsightChartMinHeight } from '../insight-detail-mode.context';
import { InsightId, InsightIdValue } from '../insight-registry/insight-ids';
import { useLast6MonthsNivalizTransactionTimerActivityWeekdayHeatmapChart } from './use-last-6-months-nivaliz-transaction-timer-activity-weekday-heatmap-chart';

type WeekdayNamedChart = typeof NivalizOutboundTransactionTimerActivityWeekdayHeatmapChart | typeof NivalizInboundTransactionTimerActivityWeekdayHeatmapChart;

const Last6MonthsNivalizTransactionTimerActivityWeekdayHeatmapChartBase = ({
    accountId,
    width,
    insightId,
    Chart,
    title
}: {
    accountId: string;
    width?: InsightCardWidth;
    insightId: InsightIdValue;
    Chart: WeekdayNamedChart;
    title: ReactNode;
}) => {
    const chartMinHeight = useInsightChartMinHeight(220);
    const { data, isLoading, rangeStart, weekCount } = useLast6MonthsNivalizTransactionTimerActivityWeekdayHeatmapChart(accountId, Chart.definition);
    const onCellClick = useInsightHeatmapCellClick({
        accountId,
        chartKey: Chart.definition.key,
        title
    });

    return (
        <InsightCard
            insightId={insightId}
            chartKey={Chart.definition.key}
            width={width}
            title={title}
            subtitle={
                <FormattedMessage
                    id='platform.analytics.insights.last-6-months-nivaliz-transaction-timer-activity-weekday-heatmap-chart.subtitle'
                    description='Subtitle on the Nivaliz activity heatmap indicating the chart covers the last six months'
                    defaultMessage='last 6 months'
                />
            }
            isLoading={isLoading}>
            {!hasChartSeries(data) || !data ? (
                <div className='text-foreground-default'>{CommonMessage.ERRORS.NO_DATA_AVAILABLE}</div>
            ) : (
                <div
                    className='w-full min-w-0 overflow-hidden'
                    style={{ height: chartMinHeight }}>
                    <Chart
                        data={data}
                        rangeStart={rangeStart}
                        weekCount={weekCount}
                        minHeight={chartMinHeight}
                        onCellClick={onCellClick}
                    />
                </div>
            )}
        </InsightCard>
    );
};

export const Last6MonthsNivalizUrnsTransactionTimerActivityWeekdayHeatmapChart = ({ accountId, width }: { accountId: string; width?: InsightCardWidth }) => (
    <Last6MonthsNivalizTransactionTimerActivityWeekdayHeatmapChartBase
        accountId={accountId}
        width={width}
        insightId={InsightId.NIVALIZ_URNS_ACTIVITY_WEEKDAY_HEATMAP}
        Chart={NivalizOutboundTransactionTimerActivityWeekdayHeatmapChart}
        title={
            <FormattedMessage
                id='platform.analytics.insights.last-6-months-nivaliz-urns-transaction-timer-activity-weekday-heatmap-chart.title'
                description='Title of the Nivaliz urns insights heatmap showing combined completed drop-off and pick-up timer volume by weekday over the last six months'
                defaultMessage='Urns: Activity heatmap'
            />
        }
    />
);

export const Last6MonthsNivalizCoffinsTransactionTimerActivityWeekdayHeatmapChart = ({ accountId, width }: { accountId: string; width?: InsightCardWidth }) => (
    <Last6MonthsNivalizTransactionTimerActivityWeekdayHeatmapChartBase
        accountId={accountId}
        width={width}
        insightId={InsightId.NIVALIZ_COFFINS_ACTIVITY_WEEKDAY_HEATMAP}
        Chart={NivalizInboundTransactionTimerActivityWeekdayHeatmapChart}
        title={
            <FormattedMessage
                id='platform.analytics.insights.last-6-months-nivaliz-coffins-transaction-timer-activity-weekday-heatmap-chart.title'
                description='Title of the Nivaliz coffins insights heatmap showing combined completed drop-off and pick-up timer volume by weekday over the last six months'
                defaultMessage='Coffins: Activity heatmap'
            />
        }
    />
);
